import React, { Component } from "react";
import { connect } from "react-redux";
import { fetchCategoryShowcases } from "../store/actions/categoryShowcase";
import { Link } from "react-router-dom";
import { css, StyleSheet } from "aphrodite";
import { PUBLIC_URL } from "../config";
import LazyImage from "./shared/LazyImage";
import { generateAltText } from "../helpers/seo";

class CategoryShowcase extends Component {
  componentDidMount() {
    this.props.fetchCategoryShowcases();
  }

  renderLoadingSkeleton = () => {
    return (
      <div className={css(styles.container)}>
        <div className="container">
          <div className={css(styles.skeletonGrid)}>
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className={css(styles.skeletonCard)}>
                <div className={css(styles.skeletonImage)}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Showcase sayısına göre dinamik grid class döndür
  getDynamicGridStyle = (showcaseCount) => {
    switch(showcaseCount) {
      case 1:
        return styles.singleGrid;
      case 2:
        return styles.doubleGrid;
      case 3:
        return styles.tripleGrid;
      case 4:
      default:
        return styles.quadGrid;
    }
  };

  // Showcase sayısına göre card style döndür
  getCardStyle = (showcaseCount, index) => {
    if (showcaseCount === 1) {
      return styles.singleCard;
    } else if (showcaseCount === 2) {
      return styles.doubleCard;
    } else if (showcaseCount === 3) {
      // 3'lü layoutta ilk card büyük, diğerleri küçük
      return index === 0 ? styles.tripleMainCard : styles.tripleSubCard;
    } else {
      // 4'lü layout - 2x2 grid
      return styles.quadCard;
    }
  };

  renderShowcaseCard = (showcase, index, totalShowcases) => {
    const cardStyle = this.getCardStyle(totalShowcases, index);
    
    return (
      <Link
        key={showcase.id}
        to={`/vitrin/${showcase.id}`}
        className={css(styles.showcaseCard, cardStyle)}
      >
        <div className={css(styles.cardInner)}>
          {showcase.image ? (
            <LazyImage
              src={PUBLIC_URL(showcase.image)}
              alt={generateAltText('showcase', showcase, showcase.title)}
              title={showcase.title}
              className={css(styles.showcaseImage)}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className={css(styles.placeholderImage)}>
              <div className={css(styles.placeholderContent)}>
                <h3 className={css(styles.placeholderTitle)}>{showcase.title}</h3>
              </div>
            </div>
          )}
        </div>
      </Link>
    );
  };

  render() {
    const { categoryShowcase } = this.props;
    const { categoryShowcases, loading, error } = categoryShowcase;

    if (loading) {
      return this.renderLoadingSkeleton();
    }

    if (error || !categoryShowcases || categoryShowcases.length === 0) {
      return null;
    }

    const showcaseCount = categoryShowcases.length;
    const gridStyle = this.getDynamicGridStyle(showcaseCount);

    return (
      <div className={css(styles.container)}>
        <div className="container">
          <div className={css(styles.showcaseGrid, gridStyle)}>
            {categoryShowcases.map((showcase, index) =>
              this.renderShowcaseCard(showcase, index, showcaseCount)
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ categoryShowcase }) => {
  return { categoryShowcase };
};

const mapDispatchToProps = {
  fetchCategoryShowcases,
};

const styles = StyleSheet.create({
  container: {
    padding: "40px 0",
    backgroundColor: "#fff",
    "@media (max-width: 768px)": {
      padding: "20px 0",
    },
  },
  
  showcaseGrid: {
    display: "grid",
    gap: "20px",
    "@media (max-width: 768px)": {
      gap: "15px",
    },
  },
  
  // 1 showcase - Tam genişlik
  singleGrid: {
    gridTemplateColumns: "1fr",
  },
  
  // 2 showcase - Yan yana
  doubleGrid: {
    gridTemplateColumns: "1fr 1fr",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  
  // 3 showcase - 1 büyük solda, 2 küçük sağda üst üste
  tripleGrid: {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "auto",
    },
  },
  
  // 4 showcase - 2x2 grid
  quadGrid: {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "auto",
    },
  },
  
  showcaseCard: {
    display: "block",
    position: "relative",
    overflow: "hidden",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    ":hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    },
  },
  
  // Tek kart - Tam genişlik, büyük boy
  singleCard: {
    gridColumn: "1 / -1",
    height: "400px",
    "@media (max-width: 768px)": {
      height: "300px",
    },
  },
  
  // Çift kart - Normal boy
  doubleCard: {
    height: "300px",
    "@media (max-width: 768px)": {
      height: "250px",
    },
  },
  
  // Üçlü ana kart - Sol taraf büyük
  tripleMainCard: {
    gridRow: "1 / -1",
    height: "100%",
    minHeight: "400px",
    "@media (max-width: 768px)": {
      gridRow: "auto",
      minHeight: "300px",
    },
  },
  
  // Üçlü yan kartlar - Sağ taraf küçük
  tripleSubCard: {
    height: "190px",
    "@media (max-width: 768px)": {
      height: "200px",
    },
  },
  
  // Dörtlü kart - 2x2 grid
  quadCard: {
    height: "200px",
    "@media (max-width: 768px)": {
      height: "180px",
    },
  },
  
  cardInner: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  
  showcaseImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  
  placeholderImage: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  
  placeholderContent: {
    textAlign: "center",
    color: "white",
    padding: "20px",
  },
  
  placeholderTitle: {
    fontSize: "24px",
    fontWeight: "600",
    margin: "0",
    "@media (max-width: 768px)": {
      fontSize: "20px",
    },
  },
  
  // Loading skeleton
  skeletonGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  
  skeletonCard: {
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
  },
  
  skeletonImage: {
    height: "250px",
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "loading 1.5s infinite",
  },
  
  "@keyframes loading": {
    "0%": {
      backgroundPosition: "200% 0",
    },
    "100%": {
      backgroundPosition: "-200% 0",
    },
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CategoryShowcase);


// WEBPACK FOOTER //
// ./src/components/CategoryShowcase.js